import { SIGNAL_QUEUE_FILED_FLOOR_DAYS } from './signal-subscriptions.js';

export type Plan = 'free' | 'starter' | 'pro';

export type BillingStatus =
  | 'active'
  | 'incomplete'
  | 'incomplete_expired'
  | 'past_due'
  | 'canceled'
  | 'unpaid';

export interface EntitlementSet {
  /** Days of signal history a workspace can see. 30 = free, null = unlimited. */
  signalHistoryDays: number | null;
  /** Effective seat limit. */
  seats: number;
  emailAlerts: boolean;
  teamsAlerts: boolean;
  /** Rolling-24h Data API credit ceiling. 0 = no API access. */
  apiCreditCeiling24h: number;
  /** Rolling-24h rows Spec's query_data may return, on its own meter apart from the Data API. */
  specQueryRows24h: number;
  /** Rolling-24h web_search calls (searches plus page reads) a workspace's Spec may make. */
  specWebCalls24h: number;
  /** Enrichment credits granted each billing period (1 credit = $1); enrichment actions only, no rollover. */
  enrichCreditsMonthly: number;
}

export const PLAN_DEFAULTS: Record<Plan, EntitlementSet> = {
  free: {
    signalHistoryDays: 30,
    seats: 1,
    emailAlerts: false,
    teamsAlerts: false,
    apiCreditCeiling24h: 0,
    specQueryRows24h: 1_000,
    specWebCalls24h: 20,
    enrichCreditsMonthly: 1,
  },
  starter: {
    signalHistoryDays: null,
    seats: 3,
    emailAlerts: true,
    teamsAlerts: false,
    apiCreditCeiling24h: 0,
    specQueryRows24h: 25_000,
    specWebCalls24h: 200,
    enrichCreditsMonthly: 50,
  },
  pro: {
    signalHistoryDays: null,
    seats: 10,
    emailAlerts: true,
    teamsAlerts: true,
    apiCreditCeiling24h: 250_000,
    specQueryRows24h: 250_000,
    specWebCalls24h: 1_000,
    enrichCreditsMonthly: 200,
  },
};

/** Statuses that keep the paid plan. Others fall back to free. */
const ENTITLED_STATUSES: ReadonlySet<BillingStatus> = new Set<BillingStatus>([
  'active',
  'past_due',
]);

/** The billing-row fields the resolvers read. Minimal and pg-free. */
export interface BillingForEntitlements {
  plan: Plan;
  status: BillingStatus;
  planOverride?: Plan | null;
  seatLimitOverride?: number | null;
  seatQuantity?: number | null;
}

/**
 * The plan the customer is BILLED on right now (no override). For billing
 * display only. past_due keeps access (Stripe is still retrying the card);
 * unpaid, canceled, incomplete, and incomplete_expired fall back to free.
 * Status-only by design: subscription presence is handled by the caller
 * (hasStripeSubscription drives checkout vs portal in the card state).
 */
export function billedPlan(input: { plan: Plan; status: BillingStatus }): Plan {
  return ENTITLED_STATUSES.has(input.status) ? input.plan : 'free';
}

/** Any plan above free is paid. Derived from the type, not hardcoded tier names. */
export function isPaidPlan(plan: Plan): plan is Exclude<Plan, 'free'> {
  return plan !== 'free';
}

/**
 * The plan whose ENTITLEMENTS apply. A manual planOverride wins over the billed
 * plan, regardless of status. Every feature gate consults this; no gate reads
 * the raw plan column directly.
 */
export function effectivePlan(billing: BillingForEntitlements): Plan {
  return billing.planOverride ?? billedPlan(billing);
}

/** Resolve the full entitlement set for a workspace billing row. */
export function entitlementsFor(billing: BillingForEntitlements): EntitlementSet {
  const base = PLAN_DEFAULTS[effectivePlan(billing)];
  const seats = billing.seatLimitOverride ?? base.seats + (billing.seatQuantity ?? 0);
  return { ...base, seats };
}

/**
 * Filed-at floor (in days) for Notifications, the per-type counts, and the
 * delivery engine: the operational freshness floor tightened by the plan's
 * history window. Free => min(90, 30) = 30; paid => min(90, Infinity) = 90.
 * Consumed in Phase 2.
 */
export function effectiveFiledFloorDays(entitlements: EntitlementSet): number {
  return Math.min(
    SIGNAL_QUEUE_FILED_FLOOR_DAYS,
    entitlements.signalHistoryDays ?? Number.POSITIVE_INFINITY,
  );
}
